import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "../Venues/VenueShow.css";
import { Grid } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import CircularProgress from "@mui/material/CircularProgress";
import BACKOFFICE_BASE_API_V1 from "../../utils/backofficeApi";
import { useAuth } from "../../contexts/AuthContext";
import {
  addFavorite,
  getMyFavorites,
  removeFavorite,
} from "../../utils/favoritesApi";
import { applyDynamicSeo } from "../../utils/dynamicSeo";
import {
  ensureOkResponse,
  handleApiFailureRedirect,
} from "../../utils/apiFailsafe";

function ProductShow() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authPayload } = useAuth();
  const [product, setProduct] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [favoriteError, setFavoriteError] = useState("");
  const [favoriteNotice, setFavoriteNotice] = useState("");

  const formatPrice = (value) => {
    if (value === null || value === undefined) {
      return null;
    }
    const raw = typeof value === "number" ? value.toFixed(0) : String(value);
    const [whole] = raw.split(".");
    return whole || null;
  };

  const productPrice = product ? formatPrice(product.price) : null;
  const isAvailable =
    product?.available === true ||
    product?.available === 1 ||
    product?.available === "true";
  const productImages =
    product?.images?.length > 0
      ? product.images
      : ["https://placehold.co/600x400/weddings/white"];

  useEffect(() => {
    let isMounted = true;

    const loadProductData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKOFFICE_BASE_API_V1}/products/${id}`);
        ensureOkResponse(response);
        const data = await response.json();

        if (!isMounted) {
          return;
        }

        setProduct(data?.product || null);
        setProducts(Array.isArray(data?.similar_products) ? data.similar_products : []);
      } catch (error) {
        if (isMounted) {
          handleApiFailureRedirect(navigate, error);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadProductData();

    return () => {
      isMounted = false;
    };
  }, [id, navigate]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [product?.id]);

  useEffect(() => {
    if (!product?.name) {
      return;
    }

    applyDynamicSeo({
      title: `${product.name} | Kazhavda`,
      description:
        product.description ||
        "Погледнете детали за свадбен продукт и слични предлози за вашиот свадбен ден.",
      imageUrl: product.images?.[0],
      type: "product",
      keywords: `свадбен продукт, ${product.name}, wedding product, kazhavda`,
    });
  }, [product]);

  useEffect(() => {
    if (!favoriteNotice) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setFavoriteNotice("");
    }, 2200);

    return () => window.clearTimeout(timeoutId);
  }, [favoriteNotice]);

  useEffect(() => {
    if (!authPayload?.id_token || !product?.id) {
      setIsFavorite(false);
      return undefined;
    }

    let isMounted = true;

    const loadFavoriteState = async () => {
      try {
        setFavoriteError("");
        const payload = await getMyFavorites();
        const productIdAsString = String(product.id);
        const productIds = Array.isArray(payload?.product_ids)
          ? payload.product_ids.map((value) => String(value))
          : [];
        const items = Array.isArray(payload?.items) ? payload.items : [];
        const fallbackFavorites = Array.isArray(payload)
          ? payload
          : Array.isArray(payload?.favorites)
            ? payload.favorites
            : [];

        const favorited =
          productIds.includes(productIdAsString) ||
          items.some(
            (item) =>
              item?.favorite_type === "product" &&
              String(item?.item_id) === productIdAsString
          ) ||
          fallbackFavorites.some(
            (favorite) =>
              favorite?.favorite_type === "product" &&
              String(favorite?.item_id) === productIdAsString
          );

        if (isMounted) {
          setIsFavorite(favorited);
        }
      } catch (error) {
        console.error("Error fetching favorites:", error);
        if (isMounted) {
          setFavoriteError("Неуспешно вчитување на омилениот статус.");
        }
      }
    };

    loadFavoriteState();

    return () => {
      isMounted = false;
    };
  }, [authPayload?.id_token, product?.id]);

  const handleFavoriteToggle = async () => {
    if (!authPayload?.id_token || !product?.id || favoriteBusy) {
      return;
    }

    const nextFavoriteState = !isFavorite;
    setFavoriteBusy(true);
    setFavoriteError("");
    setIsFavorite(nextFavoriteState);

    try {
      if (nextFavoriteState) {
        await addFavorite({
          favoriteType: "product",
          itemId: product.id,
        });
      } else {
        await removeFavorite({
          favoriteType: "product",
          itemId: product.id,
        });
      }

      setFavoriteNotice(
        nextFavoriteState ? "Додадено во омилени." : "Отстрането од омилени."
      );
    } catch (error) {
      console.error("Error updating favorite:", error);
      setIsFavorite(!nextFavoriteState);
      setFavoriteError("Неуспешно ажурирање на омилени.");
    } finally {
      setFavoriteBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="venue-container product-show">
        <div className="venue-header">
          <div className="venue-header-top">
            <button className="back-button" onClick={() => navigate(-1)}>
              <ArrowBackIosNewIcon fontSize="small" />
            </button>
          </div>
          <div className="venue-hero">
            <img
              src={"https://placehold.co/600x400/lightgray/lightgray"}
              alt="Кажав Да"
              className="venue-image"
            />
          </div>
          <div className="venue-summary">
            <span className="venue-eyebrow blurred"></span>
            <h1 className="venue-title blurred">Кажав Да</h1>
            <div className="venue-meta">
              <span className="blurred"></span>
              <span className="blurred"></span>
            </div>
            <p className="venue-description blurred"></p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="venue-container product-show">
      {product && (
        <div className="venue-header">
          <div className="venue-header-top">
            <button className="back-button" onClick={() => navigate(-1)}>
              <ArrowBackIosNewIcon fontSize="small" />
            </button>
          </div>
          <div className="venue-hero">
            <img
              src={productImages[0]}
              alt={product.name}
              className="venue-image"
              onClick={() => setIsGalleryOpen(true)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  setIsGalleryOpen(true);
                }
              }}
            />
          </div>
          <div className="venue-summary">
            <div className="venue-summary-top">
              <span className="venue-eyebrow">ПРОДУКТ</span>
              {authPayload?.id_token ? (
                <button
                  type="button"
                  className={`product-favorite-button ${
                    isFavorite ? "is-active" : ""
                  }`}
                  onClick={handleFavoriteToggle}
                  disabled={favoriteBusy}
                  aria-label={
                    isFavorite ? "Отстрани од омилени" : "Додај во омилени"
                  }
                >
                  {favoriteBusy ? (
                    <CircularProgress size={18} />
                  ) : isFavorite ? (
                    <FavoriteIcon fontSize="small" />
                  ) : (
                    <FavoriteBorderIcon fontSize="small" />
                  )}
                </button>
              ) : null}
            </div>
            <h1 className="venue-title">{product.name}</h1>
            <div className="venue-meta">
              <Link
                to={`/venue/${product.venue_id}`}
                className="venue-location-link"
              >
                {product.venue_name}
              </Link>
              <div className="venue-meta-right">
                {productPrice && (
                  <span className="venue-price">
                    {productPrice},00<small>ден</small>
                  </span>
                )}
                <span
                  className={`venue-availability ${
                    isAvailable ? "is-available" : "is-unavailable"
                  }`}
                >
                  {isAvailable ? "Достапно" : "Недостапно"}
                </span>
              </div>
            </div>
            <p className="venue-description">{product.description}</p>
            {favoriteError ? (
              <p className="favorite-feedback is-error">{favoriteError}</p>
            ) : null}
            {favoriteNotice ? (
              <p className="favorite-feedback">{favoriteNotice}</p>
            ) : null}
          </div>
        </div>
      )}
      {product && isGalleryOpen && (
        <div
          className="image-modal-backdrop"
          onClick={() => setIsGalleryOpen(false)}
        >
          <div
            className="image-modal-content"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="image-modal-close"
              onClick={() => setIsGalleryOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
            <img
              src={productImages[currentImageIndex]}
              alt={`${product.name} ${currentImageIndex + 1}`}
              className="image-modal-image"
            />
            {productImages.length > 1 && (
              <>
                <div className="image-modal-controls">
                  <button
                    className="image-modal-button"
                    onClick={() =>
                      setCurrentImageIndex(
                        (currentImageIndex - 1 + productImages.length) %
                          productImages.length
                      )
                    }
                  >
                    ‹
                  </button>
                  <button
                    className="image-modal-button"
                    onClick={() =>
                      setCurrentImageIndex(
                        (currentImageIndex + 1) % productImages.length
                      )
                    }
                  >
                    ›
                  </button>
                </div>
                <div className="image-modal-dots">
                  {productImages.map((_, index) => (
                    <button
                      key={index}
                      className={`image-modal-dot ${
                        index === currentImageIndex ? "is-active" : ""
                      }`}
                      onClick={() => setCurrentImageIndex(index)}
                      aria-label={`Image ${index + 1}`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Products Section */}
      <div className="products-section">
        <h2 className="products-section-title">Слични производи</h2>
        <Grid
          container
          className="products-list"
          spacing={2}
          direction={{ xs: "column", sm: "row" }}
          justifyContent="flex-start"
          alignItems={{ xs: "center", sm: "flex-start" }}
          wrap="wrap"
          sx={{
            px: { xs: 2, sm: 0 },
          }}
        >
          {products.length > 0 ? (
            products.map((similarProduct) => {
              const similarPrice = formatPrice(similarProduct.price);

              return (
                <Grid
                  item
                  size={{ xs: 12, sm: 6, lg: 4 }}
                  key={similarProduct.id}
                >
                  <Link
                    to={`/product/${similarProduct.id}`}
                    className="product-card-link"
                  >
                    <div className="product-card">
                      <img
                        src={
                          similarProduct.images?.[0] ||
                          "https://placehold.co/300x200"
                        }
                        alt={similarProduct.name}
                        className="product-image"
                      />
                      <div className="product-card-content">
                        <h3 className="product-title">
                          {similarProduct.name}
                        </h3>
                        <p className="product-price">
                          {similarPrice ? `${similarPrice},00` : "—"}{" "}
                          <small>ден</small>
                        </p>
                      </div>
                    </div>
                  </Link>
                </Grid>
              );
            })
          ) : (
            <div className="no-products">Нема производи...</div>
          )}
        </Grid>
        <div className="products-action">
          <Link to="/all-products" className="all-products-button">
            Сите производи
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ProductShow;
